import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import DevelopingModal from '@/presentation/components/DevelopingModal'
import LogoutConfirmModal from '@/presentation/components/LogoutConfirmModal'
import { useLogout } from '@/presentation/hooks/useLogout'
import { useUserProfile } from '@/presentation/hooks/useUserProfile'
import hiEmoji from './assets/hi-emoji.png'
import arrowRightIcon from './assets/icon-arrow-right.svg'
import changePasswordIcon from './assets/icon-change-password.svg'
import customerServiceIcon from './assets/icon-customer-service.svg'
import logoutIcon from './assets/icon-logout.svg'
import nicknameIcon from './assets/icon-nickname.svg'
import usernameCardIcon from './assets/icon-username-card.svg'

/**
 * Me 页面
 * 用户个人中心页面，展示基本资料、功能入口及退出登录
 */
export default function Me() {
  const navigate = useNavigate()
  const { userProfile: userData, isUserProfileLoading: isLoading } =
    useUserProfile()
  const { logout } = useLogout()
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false)
  const [isDevelopingModalOpen, setIsDevelopingModalOpen] = useState(false)

  const handleLogout = () => {
    logout()
    setIsLogoutModalOpen(false)
  }

  return (
    <div
      className='relative flex w-full flex-col bg-white px-[20px] pt-[20px] pb-[100px]'
      data-testid='me-page'
    >
      {/* 头部欢迎区域 */}
      <header className='flex flex-col gap-[4px]'>
        <div className='flex items-center gap-[4px]'>
          <img src={hiEmoji} alt='' className='h-[18px] w-[18px]' />
          <span className='font-black text-[16px] leading-normal text-[#1a1a1a]'>
            Hi!
          </span>
        </div>
        <h1
          className='m-0 font-black text-[24px] leading-normal text-[#1a1a1a]'
          data-testid='me-nickname'
        >
          {isLoading ? '...' : userData?.nickname || '<Unknow>'}
        </h1>
      </header>

      {/* 用户名卡片 (紫色背景) */}
      <section className='mt-[24px]'>
        <div className='flex items-center justify-between rounded-[20px] bg-[#6e59cd] p-[16px] shadow-[0px_10px_20px_rgba(110,89,205,0.25)]'>
          <div className='flex items-center gap-[8px]'>
            <div className='h-[32px] w-[32px]'>
              <img src={usernameCardIcon} alt='' className='h-full w-full' />
            </div>
            <span className='font-medium text-[14px] tracking-[0.28px] text-white'>
              Username
            </span>
          </div>
          <span
            className='text-[13px] tracking-[0.26px] text-white/80'
            data-testid='me-username'
          >
            {isLoading ? '...' : userData?.agent_name}
          </span>
        </div>
      </section>

      {/* 功能菜单列表 */}
      <section className='mt-[24px] overflow-hidden rounded-[20px] bg-[#f7f7f7]'>
        {/* 昵称项 */}
        <div
          className='flex cursor-pointer items-center justify-between p-[16px] transition-colors active:bg-black/5'
          onClick={() => navigate('/edit-nickname')}
          data-testid='nickname-section'
        >
          <div className='flex items-center gap-[8px]'>
            <img src={nicknameIcon} alt='' className='h-[32px] w-[32px]' />
            <span className='font-medium text-[14px] text-[#1a1a1a]'>
              Nickname
            </span>
          </div>
          <img
            src={arrowRightIcon}
            alt=''
            className='h-[16px] w-[16px] opacity-30 px-[2px]'
          />
        </div>

        {/* 分割线 */}
        <div className='mx-[16px] h-[0.5px] bg-black opacity-[0.05]' />

        {/* 客服项 */}
        <div
          className='flex cursor-pointer items-center justify-between p-[16px] transition-colors active:bg-black/5'
          onClick={() => setIsDevelopingModalOpen(true)}
          data-testid='customer-service-section'
        >
          <div className='flex items-center gap-[8px]'>
            <img
              src={customerServiceIcon}
              alt=''
              className='h-[32px] w-[32px]'
            />
            <span className='font-medium text-[14px] text-[#1a1a1a]'>
              Customer Service
            </span>
          </div>
          <img
            src={arrowRightIcon}
            alt=''
            className='h-[16px] w-[16px] opacity-30 px-[2px]'
          />
        </div>

        {/* 分割线 */}
        <div className='mx-[16px] h-[0.5px] bg-black opacity-[0.05]' />

        {/* 修改密码项 */}
        <div
          className='flex cursor-pointer items-center justify-between p-[16px] transition-colors active:bg-black/5'
          onClick={() => navigate('/change-password')}
          data-testid='change-password-section'
        >
          <div className='flex items-center gap-[8px]'>
            <img
              src={changePasswordIcon}
              alt=''
              className='h-[32px] w-[32px]'
            />
            <span className='font-medium text-[14px] text-[#1a1a1a]'>
              Change Password
            </span>
          </div>
          <img
            src={arrowRightIcon}
            alt=''
            className='h-[16px] w-[16px] opacity-30 px-[2px]'
          />
        </div>
      </section>

      {/* 退出登录按钮 */}
      <section className='mt-[24px]'>
        <button
          type='button'
          onClick={() => setIsLogoutModalOpen(true)}
          className='flex h-[56px] w-full items-center justify-center gap-[8px] rounded-[20px] bg-[#f7f7f7] transition-colors active:bg-black/5'
          data-testid='logout-trigger'
        >
          <img src={logoutIcon} alt='' className='h-[18px] w-[18px]' />
          <span className='font-bold text-[14px] text-[#1a1a1a]'>Sign out</span>
        </button>
      </section>

      {/* 退出确认弹窗 */}
      <LogoutConfirmModal
        isOpen={isLogoutModalOpen}
        onClose={() => setIsLogoutModalOpen(false)}
        onConfirm={handleLogout}
      />

      {/* 功能开发中弹窗 */}
      <DevelopingModal
        isOpen={isDevelopingModalOpen}
        onClose={() => setIsDevelopingModalOpen(false)}
      />
    </div>
  )
}
